import {
	useContext,
	ReactNode,
	useLayoutEffect,
	createContext,
	useState,
} from 'react';
import { useLocalStorage } from '@mantine/hooks';
import { AppUser, User, UserType } from '@/types/user';
import {
	LoginValues,
	SubmitStatus,
} from '@/components/views/unauthenticated/login/Login';
import { useQueryClient } from '@tanstack/react-query';
import { isAppError } from '@/utils/utilities';
import { AxiosError } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { ErrorCode, UUID } from '@/types/types';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import httpClient from '@/api/httpClient';
import StorageKey from '@/configs/storageKey';
import Cookies from 'js-cookie';
import UserApi from '@/api/UserApi';

type LoginResponse =
	| { status: SubmitStatus.SUCCESS }
	| { status: SubmitStatus.ERROR; stack: ErrorCode | string };

interface TokenPayload extends JwtPayload {
	id: UUID;
	user: Pick<User, 'id' | 'userType'>;
}

interface Context {
	remember: boolean;
	setRemember: (value: boolean) => void;
	user: AppUser | null;
	changeUser: (newUser?: AppUser | null) => void;
	upatedUser: (data: Partial<AppUser>) => void;
	login: (values: LoginValues) => Promise<LoginResponse>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<Context>(null!);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient();

	const [remember, setRemember] = useLocalStorage({
		key: StorageKey.REMEMBER,
		defaultValue: true,
		getInitialValueInEffect: false,
	});

	const [user, setUser] = useLocalStorage<AppUser | null>({
		key: StorageKey.USER,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [refreshTimeout, setRefreshTimeout] = useState<number>();

	useLayoutEffect(() => {
		loginWithResfreshToken();
	}, []);

	const loginWithResfreshToken = async () => {
		const refresh = Cookies.get(StorageKey.REFRESH_TOKEN);
		if (!refresh) {
			setUser(null);
			return;
		}

		try {
			const response = await UnauthenticatedApi.refreshToken(refresh);

			const { accessToken: token, refreshToken } = response.data;

			const decoded = jwtDecode<TokenPayload>(token);

			if (decoded.user.userType !== UserType.USER)
				throw new Error(ErrorCode.USER_NOT_FOUND);

			await setAuth(decoded, token, refreshToken);
		} catch (error: any) {
			console.error(error);
			if (error.code !== AxiosError.ECONNABORTED) logout();
		}
	};

	const refreshTokenWhenExpire = (token: TokenPayload) => {
		const tokenLifeSpan = token.iat && token.exp ? token.exp - token.iat : 0;
		// Refresh 5 sec before expire
		const refreshTimeout = (tokenLifeSpan - 5) * 1000;

		const id = setTimeout(() => {
			loginWithResfreshToken();
		}, refreshTimeout);

		setRefreshTimeout(id);
	};

	const login: Context['login'] = async ({ email, password }) => {
		try {
			const response = await UnauthenticatedApi.login({ email, password });

			const { accessToken: token, refreshToken } = response.data;
			const decoded = jwtDecode<TokenPayload>(token);

			if (decoded.user.userType !== UserType.USER)
				return {
					stack: ErrorCode.USER_NOT_FOUND,
					status: SubmitStatus.ERROR,
				};

			await setAuth(decoded, token, refreshToken);

			return { status: SubmitStatus.SUCCESS };
		} catch (error) {
			const stack = isAppError(error) ? error.stack : ErrorCode.GENERIC;
			return { stack, status: SubmitStatus.ERROR };
		}
	};

	const logout: Context['logout'] = async () => {
		queryClient.cancelQueries();
		Cookies.remove(StorageKey.REFRESH_TOKEN);
		httpClient.defaults.headers.common['Authorization'] = '';
		setUser(null);
		queryClient.clear();
		clearTimeout(refreshTimeout);
	};

	const setAuth = async (
		decoded: TokenPayload,
		token: string,
		refreshToken: string
	) => {
		setAuthHeader(token);

		const appUser = await UserApi.mutationGetOne(decoded.user.id);

		Cookies.set(StorageKey.REFRESH_TOKEN, refreshToken, {
			secure: true,
			expires: remember ? 360 : undefined,
		});

		refreshTokenWhenExpire(decoded);

		setUser(appUser);
	};

	const changeUser: Context['changeUser'] = (newUser = null) =>
		setUser(newUser);

	const upatedUser: Context['upatedUser'] = (data) =>
		setUser((prev) => (prev ? { ...prev, ...data } : null));

	return (
		<AuthContext.Provider
			value={{
				remember,
				setRemember,
				user,
				changeUser,
				upatedUser,
				login,
				logout,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

function setAuthHeader(token: string) {
	httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
